import { buildPayload, getFieldValueLabel } from './configurationAdminUtils';

export const VALUE_RULE_BULK_HEADERS = [
  'Id',
  'RuleGroupId',
  'FieldValueId',
  'ParentType',
  'ParentStationId',
  'ParentFieldValueId',
  'IsDisabled',
  'IsDefaultValue',
];

const asText = (value) => String(value ?? '').trim();
const asNullableNumber = (value) => asText(value) === '' ? null : Number(value);
const normalizeHeader = (value) => asText(value).toLowerCase().replace(/[^a-z0-9]/g, '');
const HEADER_BY_KEY = new Map(VALUE_RULE_BULK_HEADERS.map((header) => [normalizeHeader(header), header]));

let bulkRuleRowKey = 0;
const nextClientKey = () => `value-rule-bulk-${++bulkRuleRowKey}`;

const parseBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (value === 1) return true;
  if (value === 0 || value === null || value === undefined || value === '') return false;
  const normalized = asText(value).toLowerCase();
  if (['true', 'yes', 'y', '1'].includes(normalized)) return true;
  if (['false', 'no', 'n', '0'].includes(normalized)) return false;
  return value;
};

export const normalizeValueRuleParentType = (value, parentStationId, parentFieldValueId) => {
  const normalized = asText(value).toLowerCase().replace(/[^a-z]/g, '');
  if (['station', 'stationid', 'parentstation'].includes(normalized)) return 'station';
  if (['fieldvalue', 'dependency', 'fieldvaluedependency', 'parentfieldvalue'].includes(normalized)) return 'fieldValue';
  if (!normalized && asText(parentStationId)) return 'station';
  if (!normalized && asText(parentFieldValueId)) return 'fieldValue';
  return normalized;
};

const isBlankMatrixRow = (row = []) => row.every((value) => asText(value) === '');

export const getValueRuleBulkScopeRows = (rules = [], stationId, fieldValueIds = []) => {
  const numericStationId = Number(stationId);
  const allowedValueIds = fieldValueIds instanceof Set ? fieldValueIds : new Set(fieldValueIds);
  const branchKeys = new Set(rules
    .filter((rule) => rule.parentStationId === numericStationId && allowedValueIds.has(rule.fieldValueId))
    .map((rule) => `${rule.ruleGroupId}:${rule.fieldValueId}`));
  return rules.filter((rule) => (
    branchKeys.has(`${rule.ruleGroupId}:${rule.fieldValueId}`)
    && (
      rule.parentStationId === numericStationId
      || (rule.parentStationId === null && rule.parentFieldValueId !== null)
    )
  ));
};

export const createValueRuleBulkRows = (rules = []) => [...rules]
  .sort((left, right) => (
    left.ruleGroupId - right.ruleGroupId
    || left.fieldValueId - right.fieldValueId
    || Number(left.parentStationId === null) - Number(right.parentStationId === null)
    || left.id - right.id
  ))
  .map((rule) => ({
    clientKey: nextClientKey(),
    id: rule.id,
    ruleGroupId: rule.ruleGroupId,
    fieldValueId: rule.fieldValueId,
    parentType: rule.parentStationId !== null ? 'station' : 'fieldValue',
    parentStationId: rule.parentStationId ?? '',
    parentFieldValueId: rule.parentFieldValueId ?? '',
    isDisabled: Boolean(rule.isDisabled),
    isDefaultValue: Boolean(rule.isDefaultValue),
    isExisting: true,
  }));

export const createBlankValueRuleBulkRow = ({
  stationId,
  fieldValueId,
  ruleGroupId,
  parentType = 'station',
  parentFieldValueId = '',
  isDisabled = false,
  isDefaultValue = false,
}) => ({
  clientKey: nextClientKey(),
  id: '',
  ruleGroupId,
  fieldValueId,
  parentType,
  parentStationId: parentType === 'station' ? stationId : '',
  parentFieldValueId: parentType === 'fieldValue' ? parentFieldValueId : '',
  isDisabled,
  isDefaultValue,
  isExisting: false,
});

export const getNextBulkValueRuleGroupId = (allRules = [], stagedRows = []) => Math.max(
  0,
  ...allRules.map((rule) => Number(rule.ruleGroupId) || 0),
  ...stagedRows.map((row) => Number(row.ruleGroupId) || 0)
) + 1;

export const parseValueRuleImportMatrix = (matrix = []) => {
  const firstPopulatedRowIndex = matrix.findIndex((row) => !isBlankMatrixRow(row));
  if (firstPopulatedRowIndex < 0) throw new Error('The uploaded table is empty.');

  const rawHeaders = matrix[firstPopulatedRowIndex];
  const mappedHeaders = rawHeaders.map((header) => HEADER_BY_KEY.get(normalizeHeader(header)) || '');
  const unknownHeaders = rawHeaders.filter((header, index) => asText(header) && !mappedHeaders[index]);
  if (unknownHeaders.length > 0) throw new Error(`Unknown column${unknownHeaders.length === 1 ? '' : 's'}: ${unknownHeaders.join(', ')}.`);
  for (const requiredHeader of ['RuleGroupId', 'FieldValueId', 'ParentType']) {
    if (!mappedHeaders.includes(requiredHeader)) throw new Error(`The table must include a ${requiredHeader} column.`);
  }

  const records = matrix
    .slice(firstPopulatedRowIndex + 1)
    .filter((row) => !isBlankMatrixRow(row))
    .map((row) => Object.fromEntries(mappedHeaders
      .map((header, index) => [header, row[index]])
      .filter(([header]) => header)));
  if (records.length === 0) throw new Error('The table does not contain any rule rows.');

  return records.map((record) => {
    const parentType = normalizeValueRuleParentType(record.ParentType, record.ParentStationId, record.ParentFieldValueId);
    return {
      clientKey: nextClientKey(),
      id: asText(record.Id),
      ruleGroupId: asText(record.RuleGroupId),
      fieldValueId: asText(record.FieldValueId),
      parentType,
      parentStationId: parentType === 'station' ? asText(record.ParentStationId) : '',
      parentFieldValueId: parentType === 'fieldValue' ? asText(record.ParentFieldValueId) : '',
      isDisabled: parseBoolean(record.IsDisabled),
      isDefaultValue: parseBoolean(record.IsDefaultValue),
      isExisting: false,
    };
  });
};

export const mergeImportedValueRuleRows = (currentRows = [], importedRows = []) => {
  const importedIds = new Set();
  importedRows.forEach((row) => {
    if (!asText(row.id)) return;
    const id = Number(row.id);
    if (importedIds.has(id)) throw new Error(`The import contains duplicate ID ${id}.`);
    importedIds.add(id);
  });

  const nextRows = [...currentRows];
  importedRows.forEach((row) => {
    const numericId = Number(row.id);
    const existingIndex = row.id === '' ? -1 : nextRows.findIndex((candidate) => Number(candidate.id) === numericId);
    if (existingIndex >= 0) {
      nextRows[existingIndex] = { ...row, clientKey: nextRows[existingIndex].clientKey, isExisting: true };
    } else {
      nextRows.push(row);
    }
  });
  return nextRows;
};

const toDraft = (row) => ({
  id: row.id,
  fieldValueId: row.fieldValueId,
  ruleGroupId: row.ruleGroupId,
  parentStationId: row.parentType === 'station' ? row.parentStationId : null,
  parentFieldValueId: row.parentType === 'fieldValue' ? row.parentFieldValueId : null,
  isDisabled: row.isDisabled,
  isDefaultValue: row.isDefaultValue,
});

const payloadsMatch = (left, right) => (
  Number(left.FieldValueId) === Number(right.FieldValueId)
  && Number(left.RuleGroupId) === Number(right.RuleGroupId)
  && (left.ParentStationId ?? null) === (right.ParentStationId ?? null)
  && (left.ParentFieldValueId ?? null) === (right.ParentFieldValueId ?? null)
  && Boolean(left.IsDisabled) === Boolean(right.IsDisabled)
  && Boolean(left.IsDefaultValue) === Boolean(right.IsDefaultValue)
);

const addError = (errorsByKey, row, message) => {
  errorsByKey[row.clientKey] = [...(errorsByKey[row.clientKey] || []), message];
};

export const analyzeValueRuleBulkRows = ({
  rows = [],
  baselineRules = [],
  allRules = [],
  stationId,
  fieldId,
  fieldValues = [],
}) => {
  const baselineById = new Map(baselineRules.map((rule) => [Number(rule.id), rule]));
  const baselineGroupIds = new Set(baselineRules.map((rule) => Number(rule.ruleGroupId)));
  const baselineIds = new Set(baselineRules.map((rule) => Number(rule.id)));
  const fieldValueById = new Map(fieldValues.map((value) => [Number(value.id), value]));
  const allowedValueIds = new Set(fieldValues.filter((value) => Number(value.configFieldId) === Number(fieldId)).map((value) => Number(value.id)));
  const externalGroupIds = new Set(allRules.filter((rule) => !baselineIds.has(Number(rule.id))).map((rule) => Number(rule.ruleGroupId)));
  const errorsByKey = {};
  const ids = new Map();
  const conditions = new Map();
  const branchRows = new Map();

  rows.forEach((row, index) => {
    const numericId = Number(row.id);
    const hasId = asText(row.id) !== '';
    const ruleGroupId = Number(row.ruleGroupId);
    const fieldValueId = Number(row.fieldValueId);
    const parentStationId = asNullableNumber(row.parentStationId);
    const parentFieldValueId = asNullableNumber(row.parentFieldValueId);

    if (hasId && (!Number.isInteger(numericId) || numericId <= 0)) addError(errorsByKey, row, 'ID must be a positive whole number or blank for a new rule row.');
    if (hasId && !baselineById.has(numericId)) addError(errorsByKey, row, `ID ${row.id} is not an existing rule row in this editor.`);
    if (hasId && ids.has(numericId)) addError(errorsByKey, row, `ID ${row.id} is repeated in rows ${ids.get(numericId)} and ${index + 1}.`);
    if (hasId) ids.set(numericId, index + 1);

    if (!Number.isInteger(ruleGroupId) || ruleGroupId <= 0) addError(errorsByKey, row, 'RuleGroupId must be a positive whole number.');
    if (!Number.isInteger(fieldValueId) || !allowedValueIds.has(fieldValueId)) addError(errorsByKey, row, 'FieldValueId must be an available value for the selected field.');
    if (!['station', 'fieldValue'].includes(row.parentType)) addError(errorsByKey, row, 'ParentType must be Station or FieldValue.');
    if (typeof row.isDisabled !== 'boolean') addError(errorsByKey, row, 'IsDisabled must be TRUE or FALSE.');
    if (typeof row.isDefaultValue !== 'boolean') addError(errorsByKey, row, 'IsDefaultValue must be TRUE or FALSE.');

    if (row.parentType === 'station') {
      if (parentStationId !== Number(stationId)) addError(errorsByKey, row, `ParentStationId must be ${stationId}.`);
      if (parentFieldValueId !== null) addError(errorsByKey, row, 'A Station row cannot also have ParentFieldValueId.');
    } else if (row.parentType === 'fieldValue') {
      if (parentStationId !== null) addError(errorsByKey, row, 'A FieldValue row cannot also have ParentStationId.');
      const dependencyValue = fieldValueById.get(parentFieldValueId);
      if (!dependencyValue) addError(errorsByKey, row, 'Choose an existing ParentFieldValueId dependency.');
      else if (Number(dependencyValue.configFieldId) === Number(fieldId)) addError(errorsByKey, row, 'A dependency must come from a different configuration field.');
    }

    if (Number.isInteger(ruleGroupId) && ruleGroupId > 0 && !baselineGroupIds.has(ruleGroupId) && externalGroupIds.has(ruleGroupId)) {
      addError(errorsByKey, row, `RuleGroupId ${ruleGroupId} is already used outside this station and field.`);
    }

    if (Number.isInteger(ruleGroupId) && Number.isInteger(fieldValueId) && fieldValueId > 0) {
      const branchKey = `${ruleGroupId}:${fieldValueId}`;
      if (!branchRows.has(branchKey)) branchRows.set(branchKey, []);
      branchRows.get(branchKey).push(row);
      const conditionParent = row.parentType === 'station' ? parentStationId : parentFieldValueId;
      const conditionKey = `${branchKey}:${row.parentType}:${conditionParent}`;
      if (conditions.has(conditionKey)) addError(errorsByKey, row, `Duplicates the condition in row ${conditions.get(conditionKey)}.`);
      else conditions.set(conditionKey, index + 1);
    }
  });

  branchRows.forEach((branch, branchKey) => {
    const stationRows = branch.filter((row) => row.parentType === 'station');
    if (stationRows.length !== 1) branch.forEach((row) => addError(errorsByKey, row, `Branch ${branchKey} must contain exactly one Station row.`));
    if (new Set(branch.map((row) => row.isDisabled)).size > 1) branch.forEach((row) => addError(errorsByKey, row, `IsDisabled must match across branch ${branchKey}.`));
    if (new Set(branch.map((row) => row.isDefaultValue)).size > 1) branch.forEach((row) => addError(errorsByKey, row, `IsDefaultValue must match across branch ${branchKey}.`));
  });

  const defaultBranches = [...branchRows.entries()].filter(([, branch]) => branch.some((row) => row.isDefaultValue === true));
  if (defaultBranches.length > 1) {
    defaultBranches.forEach(([, branch]) => branch.forEach((row) => addError(errorsByKey, row, 'Only one allowed value branch can be the station default for this field.')));
  }

  const operations = [];
  let unchangedCount = 0;
  rows.forEach((row) => {
    if (errorsByKey[row.clientKey]) return;
    const hasId = asText(row.id) !== '';
    const payload = buildPayload('valueRules', toDraft(row), hasId ? 'edit' : 'create');
    if (!hasId) {
      operations.push({ method: 'POST', payload, clientKey: row.clientKey, parentType: row.parentType });
      return;
    }
    const baseline = baselineById.get(Number(row.id));
    const baselinePayload = buildPayload('valueRules', baseline, 'edit');
    if (payloadsMatch(payload, baselinePayload)) unchangedCount += 1;
    else operations.push({ method: 'PUT', payload, clientKey: row.clientKey, parentType: row.parentType });
  });
  operations.sort((left, right) => (
    Number(left.payload.RuleGroupId) - Number(right.payload.RuleGroupId)
    || Number(left.payload.FieldValueId) - Number(right.payload.FieldValueId)
    || Number(left.parentType === 'fieldValue') - Number(right.parentType === 'fieldValue')
  ));

  return {
    errorsByKey,
    errorCount: Object.values(errorsByKey).reduce((total, errors) => total + errors.length, 0),
    operations: operations.map(({ parentType, ...operation }) => operation),
    createCount: operations.filter((operation) => operation.method === 'POST').length,
    updateCount: operations.filter((operation) => operation.method === 'PUT').length,
    unchangedCount,
    groupCount: new Set(rows.map((row) => Number(row.ruleGroupId)).filter((id) => id > 0)).size,
    branchCount: branchRows.size,
  };
};

const comparableRule = (rule) => ({
  id: Number(rule.id),
  fieldValueId: Number(rule.fieldValueId),
  ruleGroupId: Number(rule.ruleGroupId),
  parentStationId: rule.parentStationId ?? null,
  parentFieldValueId: rule.parentFieldValueId ?? null,
  isDisabled: Boolean(rule.isDisabled),
  isDefaultValue: Boolean(rule.isDefaultValue),
});

export const getValueRuleSnapshotConflict = (baselineRules = [], currentRules = []) => {
  const baseline = baselineRules.map(comparableRule).sort((left, right) => left.id - right.id);
  const current = currentRules.map(comparableRule).sort((left, right) => left.id - right.id);
  return JSON.stringify(baseline) === JSON.stringify(current)
    ? ''
    : 'Value rules changed on the server after this bulk editor was opened. Close and reopen it before saving so those changes are not overwritten.';
};

export const getValueRuleTemplateRows = (rules = []) => [
  VALUE_RULE_BULK_HEADERS,
  ...rules.map((rule) => [
    rule.id,
    rule.ruleGroupId,
    rule.fieldValueId,
    rule.parentStationId !== null ? 'Station' : 'FieldValue',
    rule.parentStationId ?? '',
    rule.parentFieldValueId ?? '',
    rule.isDisabled ? 'TRUE' : 'FALSE',
    rule.isDefaultValue ? 'TRUE' : 'FALSE',
  ]),
];

export const getValueRuleOptionLabel = (value, field) => (
  `${field?.fieldName || `Field ${value?.configFieldId || '?'}`} — ${value ? getFieldValueLabel(value) : 'Unknown value'}`
);
